import { getKeitaroClickSubid, persistKeitaroClickSubid } from "./trackingStorage";
import { isIosDevice, isStandalonePwa } from "./telegramPwa";

const TRACKER_POSTBACK = "http://5.45.84.213/2dd71d1/postback";
const SENT_KEY = "installPostbackSent";
const PENDING_KEY = "installPostbackPending";
const LAST_SUBID_KEY = "installPostbackLastSubid";
const NAV_FALLBACK_KEY = "installPostbackNavFallback";

function wasSentForSubid(subid) {
  try {
    if (localStorage.getItem(SENT_KEY) !== "1") return false;
    return localStorage.getItem(LAST_SUBID_KEY) === subid;
  } catch {
    return false;
  }
}

function markSent(subid) {
  try {
    localStorage.setItem(SENT_KEY, "1");
    localStorage.setItem(LAST_SUBID_KEY, subid);
    localStorage.removeItem(PENDING_KEY);
  } catch {
    /* ignore */
  }
}

function markPending() {
  try {
    localStorage.setItem(PENDING_KEY, "1");
  } catch {
    /* ignore */
  }
}

function buildQuery(subid) {
  return `subid=${encodeURIComponent(subid)}&status=install`;
}

function getPostbackTargets(subid) {
  const q = buildQuery(subid);
  const direct = `${TRACKER_POSTBACK}?${q}`;
  const targets = [direct];

  if (typeof window !== "undefined" && window.location?.origin) {
    targets.unshift(`${window.location.origin}/keitaro-postback.php?${q}`);
  }

  return targets;
}

function dispatchUrl(url) {
  const bust = `${url}&_ts=${Date.now()}`;

  if (navigator.sendBeacon) {
    try {
      navigator.sendBeacon(bust);
    } catch {
      /* ignore */
    }
  }

  const img = new Image();
  img.referrerPolicy = "no-referrer-when-downgrade";
  img.src = bust;

  fetch(bust, {
    method: "GET",
    mode: "no-cors",
    keepalive: true,
    cache: "no-store",
  }).catch(() => {});
}

async function tryProxyPostback(subid) {
  if (typeof window === "undefined" || !window.location?.origin) {
    return false;
  }

  const q = buildQuery(subid);
  const proxyUrl = `${window.location.origin}/keitaro-postback.php?${q}&_ts=${Date.now()}`;

  try {
    const response = await fetch(proxyUrl, {
      method: "GET",
      cache: "no-store",
      credentials: "same-origin",
    });
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Keitaro install: ?subid={click_id}&status=install
 */
export async function sendInstallPostback() {
  if (typeof window === "undefined") return false;

  persistKeitaroClickSubid();
  const subid = getKeitaroClickSubid();

  if (!subid) {
    markPending();
    return false;
  }

  if (wasSentForSubid(subid)) {
    return false;
  }

  markPending();
  getPostbackTargets(subid).forEach(dispatchUrl);

  const proxyOk = await tryProxyPostback(subid);
  if (proxyOk) {
    markSent(subid);
    return true;
  }

  return false;
}

/** Top-level редірект на трекер (працює з HTTPS, якщо PHP немає на сервері). */
export function sendInstallPostbackNavigationFallback(nextUrl) {
  if (typeof window === "undefined") return false;

  persistKeitaroClickSubid();
  const subid = getKeitaroClickSubid();

  if (!subid || wasSentForSubid(subid)) {
    return false;
  }

  try {
    if (sessionStorage.getItem(NAV_FALLBACK_KEY) === "1") {
      return false;
    }
    sessionStorage.setItem(NAV_FALLBACK_KEY, "1");
  } catch {
    return false;
  }

  const q = buildQuery(subid);
  const bridge = `${window.location.origin}/keitaro-postback.html?${q}&next=${encodeURIComponent(
    nextUrl || window.location.href
  )}`;

  markSent(subid);
  window.location.replace(bridge);
  return true;
}

/**
 * Перше відкриття PWA (standalone): спочатку install-постбек, потім офер.
 * Click id у localStorage (sub3/sub2), не чекаємо sessionStorage.
 */
export async function runPostbackThenOffer(offerUrl) {
  if (typeof window === "undefined") {
    return;
  }

  persistKeitaroClickSubid();
  const subid = getKeitaroClickSubid();
  const next = offerUrl || "";

  if (!subid) {
    if (next) {
      window.location.replace(next);
    }
    return;
  }

  if (wasSentForSubid(subid)) {
    if (next) {
      window.location.replace(next);
    }
    return;
  }

  const origin = window.location.origin;
  const q = buildQuery(subid);
  const proxyUrl = next
    ? `${origin}/keitaro-postback.php?${q}&next=${encodeURIComponent(next)}`
    : `${origin}/keitaro-postback.php?${q}`;

  try {
    const probe = await fetch(`${origin}/keitaro-postback.php?${q}&_ts=${Date.now()}`, {
      method: "GET",
      cache: "no-store",
      credentials: "same-origin",
    });
    if (probe.ok) {
      markSent(subid);
      window.location.replace(proxyUrl);
      return;
    }
  } catch {
    /* PHP недоступний — fallback нижче */
  }

  const sent = await sendInstallPostback();
  if (sent && next) {
    window.location.replace(next);
    return;
  }

  if (next) {
    try {
      if (sessionStorage.getItem(NAV_FALLBACK_KEY) !== "1") {
        sendInstallPostbackNavigationFallback(next);
        return;
      }
    } catch {
      /* ignore */
    }
    window.location.replace(next);
  }
}

export function scheduleInstallPostbackBurst(options = {}) {
  const { allowNavFallback = false, nextUrl = "" } = options;

  const run = () => {
    sendInstallPostback();
  };

  run();
  [600, 1800, 4500].forEach((ms) => {
    setTimeout(() => {
      run();
      if (allowNavFallback && ms === 4500) {
        try {
          if (localStorage.getItem(PENDING_KEY) === "1") {
            sendInstallPostbackNavigationFallback(nextUrl);
          }
        } catch {
          /* ignore */
        }
      }
    }, ms);
  });
}

export function initInstallPostback() {
  if (typeof window === "undefined") return () => {};

  persistKeitaroClickSubid();

  const onInstalled = () => {
    if (!isIosDevice()) {
      scheduleInstallPostbackBurst();
    }
  };

  window.addEventListener("appinstalled", onInstalled);

  const onStandalone = () => {
    if (isStandalonePwa()) {
      scheduleInstallPostbackBurst({ allowNavFallback: true });
    }
  };

  const onVisible = () => {
    if (document.visibilityState !== "visible") return;
    try {
      if (localStorage.getItem(PENDING_KEY) === "1") {
        scheduleInstallPostbackBurst({ allowNavFallback: true });
      }
    } catch {
      /* ignore */
    }
    onStandalone();
  };

  onStandalone();
  window.addEventListener("pageshow", onStandalone);
  document.addEventListener("visibilitychange", onVisible);

  return () => {
    window.removeEventListener("appinstalled", onInstalled);
    window.removeEventListener("pageshow", onStandalone);
    document.removeEventListener("visibilitychange", onVisible);
  };
}
